// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Form, Message, Icon, Segment } from 'semantic-ui-react';
import { providerApi, Provider } from '../api/providerApi';
import { AddressForm, type AddressData } from '../components/AddressForm';
import { DEFAULT_COUNTRY, SUPPORTED_COUNTRIES } from '../config/addressConfig';

function ProviderFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const defaultCountry = SUPPORTED_COUNTRIES.find(c => c.value === DEFAULT_COUNTRY);

  const [formData, setFormData] = useState<Provider>({
    name: '',
    code: '',
    description: '',
    logoUrl: '',
    websiteUrl: '',
    contactEmail: '',
    contactPhone: '',
    email: '', // Required for LOA notifications
    contactName: '',
    isActive: true,
    // Address fields (optional for providers)
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    townCity: '',
    stateProvinceCounty: '',
    postalCode: '',
    countryCode: DEFAULT_COUNTRY,
    country: defaultCountry?.text || 'Ireland'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [portalSubdomainInput, setPortalSubdomainInput] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const loadProvider = useCallback(async (providerId: string) => {
    try {
      setLoading(true);
      const data = await providerApi.getById(providerId);
      setFormData({
        ...data,
        // Ensure address fields have default values
        addressLine1: data.addressLine1 || '',
        addressLine2: data.addressLine2 || '',
        addressLine3: data.addressLine3 || '',
        townCity: data.townCity || '',
        stateProvinceCounty: data.stateProvinceCounty || '',
        postalCode: data.postalCode || '',
        countryCode: data.countryCode || DEFAULT_COUNTRY,
        country: data.country || defaultCountry?.text || 'Ireland'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load provider');
    } finally {
      setLoading(false);
    }
  }, [defaultCountry?.text]);

  useEffect(() => {
    if (isEditMode && id) {
      loadProvider(id);
    }
  }, [id, isEditMode, loadProvider]);

  const handleAddressChange = (field: keyof AddressData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (isEditMode && id) {
        await providerApi.update(id, formData);
      } else {
        await providerApi.create(formData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/providers'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save provider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container style={{ marginTop: '50px' }}>
      <Button basic onClick={() => navigate('/providers')} style={{ marginBottom: '20px' }}>
        <Icon name='arrow left' /> Back to Providers
      </Button>

      <Header as='h1'>
        <Icon name='factory' />
        <Header.Content>
          {isEditMode ? 'Edit Provider' : 'New Provider'}
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>Provider {isEditMode ? 'updated' : 'created'} successfully!</p>
        </Message>
      )}

      <Segment>
        <Form loading={loading}>
          <Form.Group widths='equal'>
            <Form.Input
              label='Name'
              placeholder='Provider name (e.g., Zurich)'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Form.Input
              label='Code'
              placeholder='Uppercase code (e.g., ZURICH)'
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
            />
          </Form.Group>

          <Form.TextArea
            label='Description'
            placeholder='Provider description'
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <Form.Group widths='equal'>
            <Form.Input
              label='Website URL'
              placeholder='https://www.provider.com'
              value={formData.websiteUrl}
              onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
            />
            <Form.Input
              label='Logo URL'
              placeholder='https://www.provider.com/logo.png'
              value={formData.logoUrl}
              onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
            />
          </Form.Group>

          <Form.Group widths='equal'>
            <Form.Input
              label='Contact Email'
              placeholder='contact@provider.com'
              type='email'
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
            />
            <Form.Input
              label='Contact Phone'
              placeholder='+1 234 567 8900'
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
            />
          </Form.Group>

          <Header as='h4' dividing>
            <Icon name='mail' />
            LOA Notification Settings
          </Header>

          <Form.Group widths='equal'>
            <Form.Input
              label='LOA Notification Email'
              placeholder='loa-notifications@provider.com'
              type='email'
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Form.Input
              label='Contact Person Name'
              placeholder='John Smith'
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
            />
          </Form.Group>

          <Header as='h4' dividing>
            <Icon name='map marker alternate' />
            Provider Address (Optional)
            <Header.Subheader>
              Physical address for LOA documents and correspondence
            </Header.Subheader>
          </Header>

          <AddressForm
            address={{
              addressLine1: formData.addressLine1 || '',
              addressLine2: formData.addressLine2,
              addressLine3: formData.addressLine3,
              townCity: formData.townCity || '',
              stateProvinceCounty: formData.stateProvinceCounty,
              postalCode: formData.postalCode || '',
              countryCode: formData.countryCode || DEFAULT_COUNTRY,
              country: formData.country || defaultCountry?.text || 'Ireland',
            }}
            onChange={handleAddressChange}
            allOptional={true}
          />

          <Form.Checkbox
            label='Active'
            checked={formData.isActive}
            onChange={(_e, data) => setFormData({ ...formData, isActive: !!data.checked })}
            style={{ marginTop: '20px' }}
          />

          <Header as='h4' dividing style={{ marginTop: '30px' }}>
            <Icon name='globe' />
            Provider Portal
            <Header.Subheader>
              Configure the provider portal for direct provider access
            </Header.Subheader>
          </Header>

          {portalError && (
            <Message negative size='small'>
              <Icon name='warning sign' />
              {portalError}
            </Message>
          )}

          {formData.portalEnabled ? (
            <>
              <Message positive size='small'>
                <Message.Header>Portal Enabled</Message.Header>
                <p>
                  Enabled on <strong>{formData.portalEnabledAt ? new Date(formData.portalEnabledAt).toLocaleDateString() : 'N/A'}</strong> by <strong>{formData.portalEnabledBy || 'N/A'}</strong>
                </p>
              </Message>
              <Form.Group widths='equal'>
                <Form.Input label='Portal Subdomain' value={formData.portalSubdomain || ''} readOnly disabled />
                <Form.Input label='Keycloak Realm ID' value={formData.keycloakRealmId || ''} readOnly disabled />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input label='Portal Client ID' value={formData.portalClientId || ''} readOnly disabled />
                <Form.Input label='Portal URL' value={formData.portalSubdomain ? `${formData.portalSubdomain}.provider.localhost.com` : ''} readOnly disabled />
              </Form.Group>
              <Button
                negative
                size='small'
                loading={portalLoading}
                onClick={async () => {
                  if (!id || !window.confirm('Are you sure you want to disable the provider portal?')) return;
                  setPortalLoading(true);
                  setPortalError(null);
                  try {
                    const updated = await providerApi.disablePortal(id);
                    setFormData(prev => ({ ...prev, ...updated }));
                  } catch (err) {
                    setPortalError(err instanceof Error ? err.message : 'Failed to disable portal');
                  } finally {
                    setPortalLoading(false);
                  }
                }}
                style={{ marginBottom: '15px' }}
              >
                <Icon name='power off' /> Disable Provider Portal
              </Button>
            </>
          ) : isEditMode ? (
            <>
              <Form.Input
                label='Portal Subdomain'
                placeholder='e.g. acme-insurance (lowercase, hyphens allowed)'
                value={formData.portalSubdomain || portalSubdomainInput}
                onChange={(e) => {
                  if (!formData.portalSubdomain) {
                    setPortalSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  }
                }}
                disabled={!!formData.portalSubdomain}
              />
              {(formData.portalSubdomain || portalSubdomainInput) && (
                <Message info size='tiny'>
                  <Icon name='linkify' />
                  Portal URL: <strong>{(formData.portalSubdomain || portalSubdomainInput)}.provider.localhost.com</strong>
                  {' | '}Realm: <strong>provider-{formData.portalSubdomain || portalSubdomainInput}</strong>
                </Message>
              )}
              <Button
                positive
                size='small'
                loading={portalLoading}
                disabled={!(formData.portalSubdomain || portalSubdomainInput) || (formData.portalSubdomain || portalSubdomainInput || '').length < 2}
                onClick={async () => {
                  if (!id) return;
                  const subdomain = formData.portalSubdomain || portalSubdomainInput;
                  setPortalLoading(true);
                  setPortalError(null);
                  try {
                    await providerApi.enablePortal(id, subdomain);
                    // Reload provider to get all updated fields
                    const updated = await providerApi.getById(id);
                    setFormData(prev => ({
                      ...prev,
                      ...updated,
                      addressLine1: updated.addressLine1 || '',
                      addressLine2: updated.addressLine2 || '',
                      addressLine3: updated.addressLine3 || '',
                      townCity: updated.townCity || '',
                      stateProvinceCounty: updated.stateProvinceCounty || '',
                      postalCode: updated.postalCode || '',
                      countryCode: updated.countryCode || DEFAULT_COUNTRY,
                      country: updated.country || defaultCountry?.text || 'Ireland'
                    }));
                  } catch (err) {
                    setPortalError(err instanceof Error ? err.message : 'Failed to enable portal');
                  } finally {
                    setPortalLoading(false);
                  }
                }}
                style={{ marginBottom: '15px' }}
              >
                <Icon name='globe' /> {formData.portalSubdomain ? 'Re-enable Provider Portal' : 'Enable Provider Portal'}
              </Button>
            </>
          ) : (
            <Message size='small'>
              <Icon name='info circle' />
              Save the provider first, then you can enable the portal.
            </Message>
          )}

          <Button primary onClick={handleSubmit} disabled={!formData.name || !formData.code || !formData.email || loading}>
            <Icon name='save' /> {isEditMode ? 'Update' : 'Create'} Provider
          </Button>
          <Button onClick={() => navigate('/providers')}>Cancel</Button>
        </Form>
      </Segment>
    </Container>
  );
}

export default ProviderFormPage;
